import React, { useState, useRef, useEffect } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonContent,
  IonFooter,
  IonInput,
  IonButton,
  IonIcon,
  IonModal,
} from "@ionic/react";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import ReactMarkdown from "react-markdown";
import "./Home.css";
import {
  mapOutline,
  send,
} from "ionicons/icons";
import { getSessionId } from "../utils/session";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from '@capacitor/keyboard';
import { getDeviceInfo } from '../utils/deviceInfo';
import { environment } from "../environment/environment";
import {
  isWithinBacolodBounds,
  JeepneyCandidate,
  metersBetween,
  planJeepneyRoute,
} from "../utils/jeepneyPlanner";
import jeepneyRoutes from "../data/jeepneyRoutes.json";
// Add these imports
import PlaceDetailModal from "./PlaceDetailModal";
import MasskaraLoading from "../components/MasskaraLoading";
import MapModal from "./MapModal";
import CityUpdatesFeed from "../components/CityUpdatesFeed";
import ChatRouteModal, { ChatJeepneyRoutePreview } from "../components/ChatRouteModal";
import TransportRouteModal, { ChatTransportRoutePreview } from "../components/TransportRouteModal";
import { track } from "../analytics";
import "./MapModal.css";

interface Message {
  from: "user" | "ai";
  text: any;
  routePreview?: ChatJeepneyRoutePreview;
  transportPreview?: ChatTransportRoutePreview;
}

interface AskBacolodResponse {
  answer?: any;
  error?: string;
  needsJeepneyRoute?: boolean;
  // Origin/destination the AI understood from the message (any language, typos
  // corrected) — used when the app's own parsing/geocoding can't resolve a place.
  origin?: string | null;
  destination?: string | null;
}

interface AskTransportResponse {
  answer?: string;
  error?: string;
}

interface DirectionEndpoints {
  origin?: string;
  destination?: string;
}

interface ResolvedRoutePlace {
  query: string;
  normalized: string;
  resolvedLabel: string;
  lat: number;
  lng: number;
}

interface ResolvedRouteContext {
  origin?: ResolvedRoutePlace;
  destination?: ResolvedRoutePlace;
  withinBacolodCoverage: boolean;
  // Raw endpoint text the user typed, kept so we can still fall back to the
  // regional transport planner even when a place didn't geocode (e.g. a typo).
  rawOrigin?: string;
  rawDestination?: string;
}

declare global {
  interface Window {
    __bacolodGoogleMapsPromise?: Promise<typeof google>;
  }
}

const getPlaceCacheKey = (place: any) =>
  place.place_id || `${place.name || ""}|${place.vicinity || ""}`.toLowerCase();

const getPlaceCoordinates = (place: any) => {
  const lat = place?.lat ?? place?.geometry?.location?.lat;
  const lng = place?.lng ?? place?.geometry?.location?.lng;
  const normalizedLat = Number(lat);
  const normalizedLng = Number(lng);

  if (!Number.isFinite(normalizedLat) || !Number.isFinite(normalizedLng)) {
    return null;
  }

  return { lat: normalizedLat, lng: normalizedLng };
};

const hasCoordinates = (place: any) =>
  getPlaceCoordinates(place) !== null;

const CURRENT_LOCATION_LABEL = "your current location";

const DIRECTION_ALIAS_MAP: Record<string, string> = {
  sm: "SM City Bacolod",
  "sm city": "SM City Bacolod",
  "sm city bacolod": "SM City Bacolod",
  "sm bacolod": "SM City Bacolod",
  ayala: "Ayala Malls Capitol Central",
  "ayala mall": "Ayala Malls Capitol Central",
  "ayala malls": "Ayala Malls Capitol Central",
  "ayala capitol": "Ayala Malls Capitol Central",
  "ayala capitol central": "Ayala Malls Capitol Central",
  "capitol central": "Ayala Malls Capitol Central",
  robinsons: "Robinsons Bacolod",
  "robinsons bacolod": "Robinsons Bacolod",
  "lopues east": "Lopue's East Centre",
  "lopues mandalagan": "Lopue's Mandalagan",
  lasalle: "University of St. La Salle",
  "la salle": "University of St. La Salle",
  usls: "University of St. La Salle",
  "north terminal": "Northbound Terminal, Bacolod City",
  "northbound terminal": "Northbound Terminal, Bacolod City",
  "south terminal": "South Terminal, Bacolod City",
  libertad: "Libertad Public Market",
  "libertad market": "Libertad Public Market",
  mesavirre: "Mesavirre Garden Residences, Bacolod City",
  "mesa virre": "Mesavirre Garden Residences, Bacolod City",
  bredco: "BREDCO Port, Bacolod City",
  port: "BREDCO Port, Bacolod City",
};

const cleanDirectionSegment = (raw?: string) => {
  if (!raw) return "";

  return raw
    .replace(/^(?:how\s+to\s+)?(?:go|get|travel|commute|ride|reach|head|walk)\s+to\s+/i, "")
    .replace(/^(?:going|heading|walking|riding|commuting|traveling)\s+to\s+/i, "")
    .replace(/\baside-from\b/gi, "")
    .replace(/\bapart-from\b/gi, "")
    .replace(/\b(?:please|pls|po|naman|lang)\b/gi, "")
    .replace(/[?.!,]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

const normalizeDirectionAlias = (raw?: string) => {
  const cleaned = cleanDirectionSegment(raw);
  if (!cleaned) return "";

  const key = cleaned
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return DIRECTION_ALIAS_MAP[key] || cleaned;
};

const extractDirectionEndpoints = (text: string): DirectionEndpoints => {
  const normalized = text.replace(/\s+/g, " ").trim();
  const sanitized = normalized
    .replace(/\baside from\b/gi, "aside-from")
    .replace(/\bapart from\b/gi, "apart-from");

  const destinationFromStartingPointMatch = sanitized.match(
    /^(.+?)(?:,|\s)+starting\s+from\s+(.+?)(?:\s+(?:by|via|using)\b|[?.!]|$)/i
  );
  if (destinationFromStartingPointMatch) {
    return {
      origin: normalizeDirectionAlias(destinationFromStartingPointMatch[2]),
      destination: normalizeDirectionAlias(destinationFromStartingPointMatch[1]),
    };
  }

  const startingPointMatch = sanitized.match(
    /\bstarting\s+from\s+(.+?)(?:\s+(?:to|going\s+to)\b\s+(.+?))?(?:\s+(?:by|via|using)\b|[?.!]|$)/i
  );
  if (startingPointMatch) {
    return {
      origin: normalizeDirectionAlias(startingPointMatch[1]),
      destination: normalizeDirectionAlias(startingPointMatch[2]),
    };
  }

  const fromToMatch = sanitized.match(
    /\bfrom\s+(.+?)\s+(?:going\s+)?\bto\b\s+(.+?)(?:\s+(?:by|via|using)\b|[?.!]|$)/i
  );
  if (fromToMatch) {
    return {
      origin: normalizeDirectionAlias(fromToMatch[1]),
      destination: normalizeDirectionAlias(fromToMatch[2]),
    };
  }

  const toFromMatch = sanitized.match(
    /\bto\s+(.+?)\s+\bfrom\b\s+(.+?)(?:\s+(?:by|via|using)\b|[?.!]|$)/i
  );
  if (toFromMatch) {
    return {
      origin: normalizeDirectionAlias(toFromMatch[2]),
      destination: normalizeDirectionAlias(toFromMatch[1]),
    };
  }

  // Filipino "to X from Y": "... sa <destination> halin/galing/gikan/mula <origin>"
  const filToFromMatch = sanitized.match(
    /\bsa\s+(.+?)\s+(?:halin|gikan|galing|mula(?:\s+sa)?|buhat(?:\s+sa)?)\s+(.+?)(?:\s+(?:by|via|using|sakay|agi)\b|[?.!]|$)/i
  );
  if (filToFromMatch) {
    return {
      origin: normalizeDirectionAlias(filToFromMatch[2]),
      destination: normalizeDirectionAlias(filToFromMatch[1]),
    };
  }

  // Filipino "from Y to X": "halin/galing/gikan/mula <origin> ... (pa)kadto/punta sa <destination>"
  const filFromToMatch = sanitized.match(
    /\b(?:halin|gikan|galing|mula(?:\s+sa)?|buhat(?:\s+sa)?)\s+(.+?)\s+(?:pa\s*(?:kadto|dulong|punta)?|kadto|adto|punta|papunta|pupunta|padulong)?\s*(?:sa|pa)\s+(.+?)(?:\s+(?:by|via|using|sakay|agi)\b|[?.!]|$)/i
  );
  if (filFromToMatch) {
    return {
      origin: normalizeDirectionAlias(filFromToMatch[1]),
      destination: normalizeDirectionAlias(filFromToMatch[2]),
    };
  }

  const strippedIntent = sanitized
    .replace(/^(?:how\s+(?:can\s+i\s+)?(?:get|go|travel|commute|ride|reach)\s+to\s+)/i, "")
    .replace(/^(?:i\s+want\s+to\s+(?:go|get|travel)\s+to\s+)/i, "")
    .replace(/^(?:directions?\s+to\s+)/i, "")
    .trim();

  const genericToMatch = strippedIntent.match(
    /^(.+?)\s+\bto\b\s+(.+?)(?:\s+(?:by|via|using)\b|[?.!]|$)/i
  );
  if (genericToMatch) {
    return {
      origin: normalizeDirectionAlias(genericToMatch[1]),
      destination: normalizeDirectionAlias(genericToMatch[2]),
    };
  }

  const toOnlyMatch = sanitized.match(/\bto\s+(.+?)(?:\s+(?:by|via|using)\b|[?.!]|$)/i);
  const fromOnlyMatch = sanitized.match(/\bfrom\s+(.+?)(?:\s+(?:by|via|using)\b|[?.!]|$)/i);
  const destination = cleanDirectionSegment(toOnlyMatch?.[1])
    ?.replace(/\s+\bfrom\b\s+.+$/i, "")
    .trim();

  return {
    origin: normalizeDirectionAlias(fromOnlyMatch?.[1]),
    destination: normalizeDirectionAlias(destination),
  };
};

const resolveDirectionEndpointsFromConversation = (
  currentQuestion: string,
  priorMessages: Message[]
): DirectionEndpoints => {
  const current = extractDirectionEndpoints(currentQuestion);
  let origin = current.origin;
  let destination = current.destination;

  if (origin && destination) {
    return { origin, destination };
  }

  for (let i = priorMessages.length - 1; i >= 0; i -= 1) {
    const message = priorMessages[i];
    if (message.from !== "user" || typeof message.text !== "string") continue;

    const candidate = extractDirectionEndpoints(message.text);

    if (!origin && candidate.origin) {
      origin = candidate.origin;
    }

    if (!destination && candidate.destination) {
      destination = candidate.destination;
    }

    if (origin && destination) {
      break;
    }
  }

  return { origin, destination };
};

// Tagalog / Hiligaynon (Ilonggo) / Cebuano direction cues.
// how: paano/pano; go: punta/pumunta/papunta, kadto/adto/pakadto, dulong/padulong;
// from: halin, gikan, galing, mula, buhat.
const FIL_DIRECTION_INTENT =
  /\b(?:pa+no|paano|papaano|kadto|magkadto|makadto|pakadto|adto|moadto|muadto|pumunta|punta|papunta|pupunta|papuntang|patungo|padulong|dulong\s+sa|halin|gikan|galing|mula\s+sa|buhat\s+sa)\b/i;

const isDirectionIntentLike = (text: string) =>
  /\b(?:how\s+(?:can\s+i\s+)?(?:get|go|travel|commute|ride|reach)|directions?|route|commute|what\s+(?:can|should)\s+i\s+ride|how\s+do\s+i\s+get|i\s+want\s+to\s+go|can\s+i\s+go|from\s+.+\bto\b|starting\s+from|i'?m\s+from|im\s+from)\b/i.test(
    text
  ) || FIL_DIRECTION_INTENT.test(text);

const formatRouteStepText = (step: {
  mainLabel: string;
  label: string;
  distance: string;
  type?: string;
}) => {
  const distanceKm = Number.parseFloat(step.distance.replace(/[^\d.]/g, ""));
  const action = `${step.mainLabel}${step.label}`.replace(/\s+/g, " ").trim();

  if (!Number.isFinite(distanceKm)) {
    return action;
  }

  if (distanceKm < 0.05) {
    if (step.type === "walk") return `${action} - just a short walk`;
    if (step.type === "transfer") return `${action} - right at the transfer point`;
    return `${action} - right away`;
  }

  if ((step.type === "walk" || step.type === "transfer") && distanceKm < 1) {
    return `${action} - about ${Math.max(10, Math.round(distanceKm * 1000))} meters`;
  }

  return `${action} - around ${distanceKm.toFixed(1)} km`;
};

const getJeepneyRouteName = (routeIdx?: number) => {
  if (routeIdx === undefined) return "Unknown";
  return jeepneyRoutes.features[routeIdx]?.properties?.name ?? "Unknown";
};

const formatCandidateRouteSummary = (candidate: JeepneyCandidate) => {
  const first = getJeepneyRouteName(candidate.routeIdx);
  const second = candidate.segBRouteName ?? getJeepneyRouteName(candidate.segBRouteIdx);

  if (candidate.segB) {
    return `${first} then transfer to ${second}`;
  }

  return first;
};

const BACOLOD_ROUTE_LOOKUP_SUFFIX = "Bacolod City, Negros Occidental, Philippines";

const REGIONAL_ROUTE_LOOKUP_SUFFIXES = [
  BACOLOD_ROUTE_LOOKUP_SUFFIX,
  "Negros Occidental, Philippines",
  "Negros Oriental, Philippines",
  "Iloilo, Philippines",
  "Guimaras, Philippines",
];

const EXPLICIT_ROUTE_AREA_SUFFIXES: {
  pattern: RegExp;
  suffix: string;
  region: string;
  landmark?: boolean;
}[] = [
  { pattern: /\bbacolod\b/i, suffix: BACOLOD_ROUTE_LOOKUP_SUFFIX, region: "bacolod" },

  // ── Negros Occidental — cities ──
  { pattern: /\bbago\b/i, suffix: "Bago City, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bcadiz\b/i, suffix: "Cadiz City, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bescalante\b/i, suffix: "Escalante City, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bhimamaylan\b/i, suffix: "Himamaylan City, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bkabankalan\b/i, suffix: "Kabankalan City, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bla carlota\b/i, suffix: "La Carlota City, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bsagay\b/i, suffix: "Sagay City, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bsan carlos\b/i, suffix: "San Carlos City, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bsilay\b/i, suffix: "Silay City, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bsipalay\b/i, suffix: "Sipalay City, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\btalisay\b/i, suffix: "Talisay City, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bvictorias\b/i, suffix: "Victorias City, Negros Occidental, Philippines", region: "negros-occidental" },

  // ── Negros Occidental — municipalities ──
  { pattern: /\bbinalbagan\b/i, suffix: "Binalbagan, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bcalatrava\b/i, suffix: "Calatrava, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bcandoni\b/i, suffix: "Candoni, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bcauayan\b/i, suffix: "Cauayan, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bmagalona\b/i, suffix: "E.B. Magalona, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bhinigaran\b/i, suffix: "Hinigaran, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bhinoba-?an\b/i, suffix: "Hinoba-an, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bilog\b/i, suffix: "Ilog, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bisabela\b/i, suffix: "Isabela, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bla castellana\b/i, suffix: "La Castellana, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bmanapla\b/i, suffix: "Manapla, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bmoises padilla\b/i, suffix: "Moises Padilla, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bmurcia\b/i, suffix: "Murcia, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bpontevedra\b/i, suffix: "Pontevedra, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bpulupandan\b/i, suffix: "Pulupandan, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\b(?:dsb|(?:don )?salvador benedicto)\b/i, suffix: "Don Salvador Benedicto, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bsan enrique\b/i, suffix: "San Enrique, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\btoboso\b/i, suffix: "Toboso, Negros Occidental, Philippines", region: "negros-occidental" },
  { pattern: /\bvalladolid\b/i, suffix: "Valladolid, Negros Occidental, Philippines", region: "negros-occidental" },

  // ── Negros Oriental — cities ──
  { pattern: /\bbais\b/i, suffix: "Bais City, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bbayawan\b/i, suffix: "Bayawan City, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bcanlaon\b/i, suffix: "Canlaon City, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bdumaguete\b/i, suffix: "Dumaguete City, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bguihulngan\b/i, suffix: "Guihulngan City, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\btanjay\b/i, suffix: "Tanjay City, Negros Oriental, Philippines", region: "negros-oriental" },

  // ── Negros Oriental — municipalities ──
  { pattern: /\bamlan\b/i, suffix: "Amlan, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bayungon\b/i, suffix: "Ayungon, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bbacong\b/i, suffix: "Bacong, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bbasay\b/i, suffix: "Basay, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bbindoy\b/i, suffix: "Bindoy, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bdauin\b/i, suffix: "Dauin, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bjimalalud\b/i, suffix: "Jimalalud, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bla libertad\b/i, suffix: "La Libertad, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bmabinay\b/i, suffix: "Mabinay, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bmanjuyod\b/i, suffix: "Manjuyod, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bpamplona\b/i, suffix: "Pamplona, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bsan jose\b/i, suffix: "San Jose, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bsanta catalina\b/i, suffix: "Santa Catalina, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bsiaton\b/i, suffix: "Siaton, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bsibulan\b/i, suffix: "Sibulan, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\btayasan\b/i, suffix: "Tayasan, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bvalencia\b/i, suffix: "Valencia, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bvallehermoso\b/i, suffix: "Vallehermoso, Negros Oriental, Philippines", region: "negros-oriental" },
  { pattern: /\bzamboanguita\b/i, suffix: "Zamboanguita, Negros Oriental, Philippines", region: "negros-oriental" },

  // ── Well-known landmarks / resorts outside Bacolod (name ≠ its town) ──
  { pattern: /\blakawon\b/i, suffix: "Lakawon Island, Cadiz City, Negros Occidental, Philippines", region: "negros-occidental", landmark: true },
  { pattern: /\bthe ruins\b/i, suffix: "The Ruins, Talisay City, Negros Occidental, Philippines", region: "negros-occidental", landmark: true },
  { pattern: /\bmambukal\b/i, suffix: "Mambukal Mountain Resort, Murcia, Negros Occidental, Philippines", region: "negros-occidental", landmark: true },
  { pattern: /\bcampuestohan\b/i, suffix: "Campuestohan Highland Resort, Talisay City, Negros Occidental, Philippines", region: "negros-occidental", landmark: true },
  { pattern: /\bcarbin reef\b/i, suffix: "Carbin Reef, Sagay City, Negros Occidental, Philippines", region: "negros-occidental", landmark: true },
  { pattern: /\bbalay negrense\b/i, suffix: "Balay Negrense, Silay City, Negros Occidental, Philippines", region: "negros-occidental", landmark: true },

  // ── Nearby Visayas ──
  { pattern: /\biloilo\b/i, suffix: "Iloilo City, Iloilo, Philippines", region: "iloilo" },
  { pattern: /\bguimaras\b/i, suffix: "Guimaras, Philippines", region: "guimaras" },
];

const hasRegionalRouteQualifier = (value: string) =>
  /\b(?:bacolod|negros|iloilo|guimaras|philippines)\b/i.test(
    value
  );

const findExplicitRouteArea = (value?: string) => {
  if (!value) return null;
  return EXPLICIT_ROUTE_AREA_SUFFIXES.find((item) => item.pattern.test(value)) || null;
};

// For a known landmark whose name differs from its town (e.g. "lakawon" →
// "Lakawon Island, Cadiz City…"), return the full disambiguated label so the
// transport planner knows exactly which place is meant. Towns/cities are left
// as-is (a bare "SM City Bacolod" must not collapse to "Bacolod City").
const disambiguateLandmarkLabel = (label?: string) => {
  if (!label) return label;
  const area = findExplicitRouteArea(label);
  return area?.landmark ? area.suffix : label;
};

// Trim a verbose geocoder label down to its first couple of segments for a tidy
// map caption, e.g. "Lakawon Island, Cadiz City, Negros Occidental, Philippines"
// → "Lakawon Island, Cadiz City".
const cleanTransportLabel = (label?: string) => {
  if (!label) return label ?? "";
  return label.split(",").slice(0, 2).join(",").trim();
};

// Google sometimes resolves a named place (e.g. "Sitari") to the correct spot
// but returns only the street/corner as the formatted address, dropping the
// name. Keep the searched name when the formatted address doesn't already
// contain it — coordinates are unaffected, this only fixes the display label.
const buildResolvedLabel = (searchedName: string, formatted: string) => {
  const token = (searchedName || "").split(",")[0].trim();
  if (!token || formatted.toLowerCase().includes(token.toLowerCase())) {
    return formatted;
  }
  const display =
    token === token.toLowerCase()
      ? token.replace(/\b\w/g, (c) => c.toUpperCase())
      : token;
  return `${display}, ${formatted}`;
};

// A geocode result that only partially matched the query and degraded to a whole
// city/province (e.g. "lakwon" → "Bacolod") is a fuzzy fallback, not a real match.
const FUZZY_BROAD_AREA_TYPES = [
  "locality",
  "administrative_area_level_1",
  "administrative_area_level_2",
  "administrative_area_level_3",
  "country",
  "political",
];
const isFuzzyBroadGeocode = (
  top?: (google.maps.GeocoderResult & { partial_match?: boolean }) | null
) =>
  Boolean(
    top?.partial_match &&
      Array.isArray(top.types) &&
      top.types.length > 0 &&
      top.types.every((t) => FUZZY_BROAD_AREA_TYPES.includes(t))
  );

// Bacolod / Negros + nearby Visayas — the area this transport guide serves. Any
// resolved point outside this box (e.g. a tester's current location in the US,
// or a far province) must not drive a route or a map.
const SERVICE_REGION_BOUNDS = { north: 11.8, south: 8.4, east: 124.8, west: 121.4 };
const isWithinServiceRegion = (p: { lat: number; lng: number }) =>
  p.lat <= SERVICE_REGION_BOUNDS.north &&
  p.lat >= SERVICE_REGION_BOUNDS.south &&
  p.lng <= SERVICE_REGION_BOUNDS.east &&
  p.lng >= SERVICE_REGION_BOUNDS.west;

const isLikelyLocalShortcut = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (hasRegionalRouteQualifier(trimmed)) return false;

  const tokens = trimmed.split(/\s+/).filter(Boolean);
  return tokens.length <= 3 && trimmed.length <= 32;
};

const buildRegionalRouteLookupCandidates = (value: string, counterpart?: string) => {
  const trimmed = value.trim();
  if (!trimmed) return [];

  if (hasRegionalRouteQualifier(trimmed)) {
    return [trimmed];
  }

  const explicitArea = findExplicitRouteArea(trimmed);
  if (explicitArea) {
    const appendSuffix = explicitArea.suffix.replace(/^[^,]+,\s*/, "");
    return [trimmed.includes(appendSuffix) ? trimmed : `${trimmed}, ${appendSuffix}`];
  }

  const counterpartArea = findExplicitRouteArea(counterpart);
  const preferStrictBacolod =
    counterpartArea?.region === "bacolod" && isLikelyLocalShortcut(trimmed);
  const preferBacolodFirst =
    !counterpartArea && isLikelyLocalShortcut(trimmed);

  if (preferStrictBacolod) {
    return [`${trimmed}, ${BACOLOD_ROUTE_LOOKUP_SUFFIX}`];
  }

  const suffixes =
    counterpartArea?.region === "bacolod" || preferBacolodFirst
      ? REGIONAL_ROUTE_LOOKUP_SUFFIXES
      : [
          "Negros Occidental, Philippines",
          BACOLOD_ROUTE_LOOKUP_SUFFIX,
          "Negros Oriental, Philippines",
          "Iloilo, Philippines",
          "Guimaras, Philippines",
        ];

  return suffixes.map((suffix) => `${trimmed}, ${suffix}`);
};

const Chat: React.FC = () => {
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const [isPlaceModalOpen, setIsPlaceModalOpen] = useState(false);
  const [vicinityOptions, setVicinityOptions] = useState<string[]>([]);
  const [isVicinitySelectorOpen, setIsVicinitySelectorOpen] = useState(false);
  const [pendingPlace, setPendingPlace] = useState<{ name: string; vicinity: string; place_id?: string } | null>(null);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [mapPlaces, setMapPlaces] = useState<any[]>([]);
  const [selectedRoutePreview, setSelectedRoutePreview] = useState<ChatJeepneyRoutePreview | null>(null);
  const [selectedTransportPreview, setSelectedTransportPreview] = useState<ChatTransportRoutePreview | null>(null);

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const isKeyboardOpen = useRef(false);
  const contentRef = useRef<HTMLIonContentElement>(null);
  const placeCoordsCacheRef = useRef<Map<string, any>>(new Map());
  const placeGeometryRequestRef = useRef<Map<string, Promise<any>>>(new Map());
  const sessionId = getSessionId();
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [hasShownClickHint, setHasShownClickHint] = useState(() => {
    return localStorage.getItem('hasShownClickHint') === 'true';
  });
  const [glowMessageIndex, setGlowMessageIndex] = useState<number | null>(null);
  const [showLoading, setShowLoading] = useState(false);
  // Add this ref near your other refs
  const bgRef = useRef<HTMLDivElement>(null);

  const askBacolodAssistant = async (
    question: string,
    routeContext?: ResolvedRouteContext | null
  ): Promise<AskBacolodResponse> => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 1500;

    const fetchWithRetry = async (attempt: number): Promise<AskBacolodResponse> => {
      try {
        const { platform, deviceId, appVersion } = await getDeviceInfo();
        const res = await fetch("https://askbacolodv3-lrenkrr2bq-uc.a.run.app", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            question,
            sessionId,
            lat: location?.lat ?? null,
            lng: location?.lng ?? null,
            routeContext,
            platform,
            deviceId,
            appVersion,
          }),
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return await res.json();
      } catch (error) {
        if (attempt < MAX_RETRIES) {
          console.warn(`Attempt ${attempt} failed, retrying in ${RETRY_DELAY_MS}ms...`);
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
          return fetchWithRetry(attempt + 1);
        }
        throw error;
      }
    };

    return fetchWithRetry(1);
  };

  const askRegionalTransport = async (
    origin: string,
    destination: string
  ): Promise<AskTransportResponse> => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 1500;

    const fetchWithRetry = async (attempt: number): Promise<AskTransportResponse> => {
      try {
        const res = await fetch("https://asktransport-lrenkrr2bq-uc.a.run.app", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ origin, destination }),
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return await res.json();
      } catch (error) {
        if (attempt < MAX_RETRIES) {
          console.warn(`Transport attempt ${attempt} failed, retrying in ${RETRY_DELAY_MS}ms...`);
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
          return fetchWithRetry(attempt + 1);
        }
        throw error;
      }
    };

    return fetchWithRetry(1);
  };

  const loadGoogleMapsSdk = async () => {
    if (typeof window === "undefined") {
      throw new Error("Google Maps can only load in the browser.");
    }

    if (window.google?.maps?.Geocoder && window.google?.maps?.DirectionsService) {
      return window.google;
    }

    if (window.__bacolodGoogleMapsPromise) {
      return window.__bacolodGoogleMapsPromise;
    }

    window.__bacolodGoogleMapsPromise = new Promise<typeof google>((resolve, reject) => {
      const existing = document.querySelector(
        'script[data-bacolod-google-maps="true"]'
      ) as HTMLScriptElement | null;

      if (existing) {
        existing.addEventListener("load", () => {
          if (window.google?.maps) resolve(window.google);
        });
        existing.addEventListener("error", () => reject(new Error("Failed to load Google Maps.")));
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.dataset.bacolodGoogleMaps = "true";
      script.onload = () => {
        if (window.google?.maps) resolve(window.google);
        else reject(new Error("Google Maps loaded, but the API is unavailable."));
      };
      script.onerror = () => reject(new Error("Failed to load Google Maps."));
      document.head.appendChild(script);
    });

    return window.__bacolodGoogleMapsPromise;
  };

  const geocodeAddress = async (query: string, counterpart?: string) => {
    const maps = await loadGoogleMapsSdk();
    const geocoder = new maps.maps.Geocoder();
    const geocodeLookup = (address: string) =>
      new Promise<{ lat: number; lng: number } | null>((resolve) => {
        geocoder.geocode(
          {
            address,
            region: "PH",
            componentRestrictions: { country: "PH" },
          },
          (results, status) => {
            const top = results?.[0] as
              | (google.maps.GeocoderResult & { partial_match?: boolean })
              | undefined;
            if (status === "OK" && top?.geometry?.location) {
              // Reject the same fuzzy "unknown → whole city" fallback that
              // resolveRoutePlace guards against, so a bad match here doesn't
              // sneak a bogus in-Bacolod jeepney route through.
              if (isFuzzyBroadGeocode(top)) {
                resolve(null);
                return;
              }
              const gLat = top.geometry.location.lat();
              const gLng = top.geometry.location.lng();
              if (!isWithinServiceRegion({ lat: gLat, lng: gLng })) {
                resolve(null);
                return;
              }
              resolve({ lat: gLat, lng: gLng });
              return;
            }
            resolve(null);
          }
        );
      });

    const candidates = buildRegionalRouteLookupCandidates(query, counterpart);
    for (const candidate of candidates) {
      const result = await geocodeLookup(candidate);
      if (result) {
        return result;
      }
    }

    return null;
  };

  const resolveRoutePlace = async (
    query: string,
    counterpart?: string
  ): Promise<ResolvedRoutePlace | null> => {
    const maps = await loadGoogleMapsSdk();
    const geocoder = new maps.maps.Geocoder();
    const normalized = normalizeDirectionAlias(query);
    const resolveLookup = (address: string) =>
      new Promise<ResolvedRoutePlace | null>((resolve) => {
        geocoder.geocode(
          {
            address,
            region: "PH",
            componentRestrictions: { country: "PH" },
          },
          (results, status) => {
            const top = results?.[0] as
              | (google.maps.GeocoderResult & { partial_match?: boolean })
              | undefined;
            if (status === "OK" && top?.geometry?.location) {
              // Reject fuzzy fallbacks: Google matched only part of the query
              // (e.g. an unknown landmark like "lakawon") and degraded to a whole
              // city/province. Real places and exact city names still pass.
              if (isFuzzyBroadGeocode(top)) {
                resolve(null);
                return;
              }
              const rLat = top.geometry.location.lat();
              const rLng = top.geometry.location.lng();
              if (!isWithinServiceRegion({ lat: rLat, lng: rLng })) {
                resolve(null);
                return;
              }
              resolve({
                query,
                normalized,
                resolvedLabel: buildResolvedLabel(normalized, top.formatted_address || normalized),
                lat: rLat,
                lng: rLng,
              });
              return;
            }
            resolve(null);
          }
        );
      });

    const candidates = buildRegionalRouteLookupCandidates(normalized, counterpart);
    for (const candidate of candidates) {
      const result = await resolveLookup(candidate);
      if (result) {
        return result;
      }
    }

    return null;
  };

  const buildResolvedRouteContext = async (
    question: string,
    priorMessages: Message[]
  ): Promise<ResolvedRouteContext | null> => {
    const recentUserMessages = priorMessages
      .filter((message) => message.from === "user" && typeof message.text === "string")
      .slice(-3);

    const looksDirectionLike =
      isDirectionIntentLike(question) ||
      recentUserMessages.some((message) => isDirectionIntentLike(String(message.text)));

    if (!looksDirectionLike) {
      return null;
    }

    const endpoints = resolveDirectionEndpointsFromConversation(question, priorMessages);
    if (!endpoints.origin && !endpoints.destination) {
      return null;
    }

    const [origin, destination] = await Promise.all([
      endpoints.origin
        ? resolveRoutePlace(endpoints.origin, endpoints.destination)
        : Promise.resolve(null),
      endpoints.destination
        ? resolveRoutePlace(endpoints.destination, endpoints.origin)
        : Promise.resolve(null),
    ]);

    if (!origin && !destination) {
      // Geocoding failed for both endpoints, but we still have the raw typed
      // text — keep it so the askTransport fallback can still run (e.g. a typo
      // like "mersavirre", or an out-of-Bacolod place the geocoder rejected).
      return {
        origin: undefined,
        destination: undefined,
        withinBacolodCoverage: false,
        rawOrigin: endpoints.origin || undefined,
        rawDestination: endpoints.destination || undefined,
      };
    }

    const withinBacolodCoverage = Boolean(
      origin &&
      destination &&
      isWithinBacolodBounds({ lat: origin.lat, lng: origin.lng }) &&
      isWithinBacolodBounds({ lat: destination.lat, lng: destination.lng })
    );

    return {
      origin: origin || undefined,
      destination: destination || undefined,
      withinBacolodCoverage,
      rawOrigin: endpoints.origin || undefined,
      rawDestination: endpoints.destination || undefined,
    };
  };

  const getWalkingDistanceMeters = async (
    origin: { lat: number; lng: number },
    destination: { lat: number; lng: number }
  ) => {
    const maps = await loadGoogleMapsSdk();
    const directionsService = new maps.maps.DirectionsService();

    return new Promise<number>((resolve) => {
      directionsService.route(
        {
          origin,
          destination,
          travelMode: maps.maps.TravelMode.WALKING,
        },
        (result, status) => {
          if (status === "OK" && result?.routes?.[0]?.legs?.[0]?.distance?.value) {
            resolve(result.routes[0].legs[0].distance.value);
            return;
          }
          resolve(metersBetween(origin, destination));
        }
      );
    });
  };

  const buildChatJeepneyRouteBundle = async (
    question: string,
    priorMessages: Message[],
    routeContext?: ResolvedRouteContext | null
  ) => {
    if (
      routeContext &&
      ((routeContext.origin &&
        !isWithinBacolodBounds({ lat: routeContext.origin.lat, lng: routeContext.origin.lng })) ||
        (routeContext.destination &&
          !isWithinBacolodBounds({ lat: routeContext.destination.lat, lng: routeContext.destination.lng })) ||
        (routeContext.origin && routeContext.destination && !routeContext.withinBacolodCoverage))
    ) {
      return null;
    }

    const fallbackEndpoints = resolveDirectionEndpointsFromConversation(question, priorMessages);
    const resolvedOrigin = routeContext?.origin;
    const resolvedDestination = routeContext?.destination;

    const origin = resolvedOrigin?.resolvedLabel || fallbackEndpoints.origin;
    const destination = resolvedDestination?.resolvedLabel || fallbackEndpoints.destination;
    const usingCurrentLocation = !origin && !!destination && !!location;
    const effectiveOrigin = origin ?? (usingCurrentLocation ? CURRENT_LOCATION_LABEL : undefined);
    const effectiveOriginCoords = usingCurrentLocation
      ? location ?? undefined
      : resolvedOrigin
        ? { lat: resolvedOrigin.lat, lng: resolvedOrigin.lng }
        : undefined;
    const effectiveDestinationCoords = resolvedDestination
      ? { lat: resolvedDestination.lat, lng: resolvedDestination.lng }
      : undefined;

    if (!effectiveOrigin || !destination) {
      return null;
    }

    const [originCoords, destinationCoords] = await Promise.all([
      effectiveOriginCoords
        ? Promise.resolve(effectiveOriginCoords)
        : resolveRoutePlace(effectiveOrigin, destination).then((result) =>
            result ? { lat: result.lat, lng: result.lng } : geocodeAddress(effectiveOrigin, destination)
          ),
      effectiveDestinationCoords
        ? Promise.resolve(effectiveDestinationCoords)
        : resolveRoutePlace(destination, effectiveOrigin).then((result) =>
            result ? { lat: result.lat, lng: result.lng } : geocodeAddress(destination, effectiveOrigin)
          ),
    ]);

    if (!originCoords || !destinationCoords) {
      return null;
    }

    if (!isWithinBacolodBounds(originCoords) || !isWithinBacolodBounds(destinationCoords)) {
      return null;
    }

    const plan = await planJeepneyRoute(originCoords, destinationCoords, {
      getWalkDistanceMeters: getWalkingDistanceMeters,
    });

    if (plan.status !== "ok") {
      return null;
    }

    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    // Highlight the quoted jeepney name (e.g. Ride "Bata–Libertad" jeepney) so
    // it stands out as a bold accent pill in the chat.
    const highlightJeepneyName = (escaped: string) =>
      escaped.replace(
        /"([^"]+)"/g,
        '<span class="jeepney-name">$1</span>'
      );

    const routeLines = plan.steps.map((step, index) => {
      const escaped = escapeHtml(formatRouteStepText(step));
      const html = step.type === "jeepney" ? highlightJeepneyName(escaped) : escaped;
      return `${index + 1}. ${html}`;
    });
    const otherRouteLines = plan.topCandidates
      .slice(1)
      .map(
        (candidate, index) =>
          `${index + 1}. ${highlightJeepneyName(escapeHtml(formatCandidateRouteSummary(candidate)))}`
      );

    let routeHtml =
      `<br />` +
      `<strong>Recommended Bacolod jeepney route:</strong><br />` +
      routeLines.join("<br />");

    if (otherRouteLines.length > 0) {
      routeHtml +=
        `<br /><br />` +
        `<strong>Other jeepney options:</strong><br />` +
        otherRouteLines.join("<br />");
    }

    const preview: ChatJeepneyRoutePreview = {
      originLabel: effectiveOrigin,
      destinationLabel: destination,
      steps: plan.steps,
      topOptions: plan.topCandidates.slice(1).map((candidate) => formatCandidateRouteSummary(candidate)),
      segmentA: plan.best?.segA ?? [],
      segmentB: plan.best?.segB ?? [],
      fullRouteA: plan.fullRouteA,
      fullRouteB: plan.fullRouteB,
      walkTo: plan.best?.walkTo ?? null,
      transfer: plan.best?.transfer ?? null,
      walkFrom: plan.best?.walkFrom ?? null,
    };

    return {
      text: routeHtml,
      preview,
    };
  };

  const appendJeepneyRouteToAnswer = (answer: any, routeText: string) => {
    if (answer && typeof answer === "object" && Array.isArray(answer.recommendations)) {
      return {
        ...answer,
        footer: [answer.footer, routeText].filter(Boolean).join("\n\n"),
      };
    }

    if (typeof answer === "string") {
      return `${answer}\n\n${routeText}`;
    }

    return routeText;
  };

  const normalizeOutsideCoverageTransportAnswer = (
    answer: any,
    routeContext?: ResolvedRouteContext | null
  ) => {
    if (typeof answer !== "string" || !routeContext?.origin || !routeContext?.destination) {
      return answer;
    }

    const originInBacolod = isWithinBacolodBounds({
      lat: routeContext.origin.lat,
      lng: routeContext.origin.lng,
    });
    const destinationInBacolod = isWithinBacolodBounds({
      lat: routeContext.destination.lat,
      lng: routeContext.destination.lng,
    });

    const mentionsVerifiedJeepneyRoute =
      /\b(?:most practical|best budget-friendly|best)\s+jeepney route\b/i.test(answer) ||
      /\bhere'?s\s+the\s+most\s+practical\s+jeepney route\b/i.test(answer) ||
      /\bhere'?s\s+the\s+best\s+budget-friendly\s+jeepney route\b/i.test(answer);

    if (!mentionsVerifiedJeepneyRoute) {
      return answer;
    }

    const originLabel = routeContext.origin.resolvedLabel || routeContext.origin.normalized;
    const destinationLabel =
      routeContext.destination.resolvedLabel || routeContext.destination.normalized;

    if (originInBacolod !== destinationInBacolod) {
      return `This trip from ${originLabel} to ${destinationLabel} is only partly within Bacolod city jeepney coverage, so I can’t show one verified Bacolod jeepney route for the full journey. The simplest option is usually a direct taxi or airport/city transfer. If you want the cheaper commute, I can still help break it into two parts: the transfer into Bacolod first, then the local city ride for the last leg.`;
    }

    return `This trip from ${originLabel} to ${destinationLabel} is outside Bacolod city jeepney coverage, so I can’t show one verified Bacolod jeepney route for the full journey. The most practical option is usually an intercity transfer, bus/van, or direct taxi depending on the route.`;
  };

  // When the AI expected an appended Bacolod jeepney route but the app could not
  // build one (trip outside jeepney coverage, e.g. from the airport, or an
  // endpoint that didn't resolve), avoid leaving the AI's dangling lead-in.
  const buildNoJeepneyFallbackAnswer = async (
    routeContext: ResolvedRouteContext | null | undefined,
    question: string,
    priorMessages: Message[],
    previousAnswer: any,
    aiOrigin?: string | null,
    aiDestination?: string | null
  ): Promise<{ answer: any; transportPreview?: ChatTransportRoutePreview }> => {
    // Label priority: the app's own geocoded label first, then the AI's
    // understood place (smart with typos/other languages — used when the app
    // couldn't match), then the raw typed text as a last resort.
    const parsed = resolveDirectionEndpointsFromConversation(question, priorMessages);
    const originLabel = disambiguateLandmarkLabel(
      routeContext?.origin?.resolvedLabel ||
        routeContext?.origin?.normalized ||
        aiOrigin ||
        routeContext?.rawOrigin ||
        parsed.origin
    );
    const destinationLabel = disambiguateLandmarkLabel(
      routeContext?.destination?.resolvedLabel ||
        routeContext?.destination?.normalized ||
        aiDestination ||
        routeContext?.rawDestination ||
        parsed.destination
    );

    // Resolve both endpoints first. Only lean on the transport planner when we
    // could actually locate both places (a real, confident trip) — and use their
    // coordinates for the route map. If a place didn't resolve (a typo or unknown
    // name), skip askTransport and just show the AI's original reply instead.
    if (originLabel && destinationLabel) {
      const [originPlace, destinationPlace] = await Promise.all([
        resolveRoutePlace(originLabel).catch(() => null),
        resolveRoutePlace(destinationLabel).catch(() => null),
      ]);

      if (originPlace && destinationPlace) {
        try {
          const regional = await askRegionalTransport(originLabel, destinationLabel);
          const regionalAnswer = regional.answer || regional.error;
          if (regionalAnswer) {
            return {
              answer: normalizeOutsideCoverageTransportAnswer(regionalAnswer, routeContext),
              transportPreview: {
                originLabel: cleanTransportLabel(originLabel),
                destinationLabel: cleanTransportLabel(destinationLabel),
                origin: { lat: originPlace.lat, lng: originPlace.lng },
                destination: { lat: destinationPlace.lat, lng: destinationPlace.lng },
              },
            };
          }
        } catch (error) {
          console.error("askTransport fallback failed:", error);
        }
      }
    }

    // No confident route: show the AI's original askBacolod reply as-is.
    return { answer: previousAnswer };
  };


  const sendMessage = async () => {
    if (!input.trim() || isSending) return;
    setIsSending(true);

    const messageToSend = input.trim();
    if (!messageToSend) return;

    setInput("");

    const userMessage: Message = { from: "user", text: messageToSend };
    setMessages((prev) => [...prev, userMessage]);
    const conversationBeforeReply = [...messages, userMessage];

    track("chat_message_sent", { chars: messageToSend.length });

    const lastNotif = localStorage.getItem('lastPushNotification');
    let messageWithContext = messageToSend;
    if (lastNotif) {
      const notifData = JSON.parse(lastNotif);
      messageWithContext = `Earlier, the app received a push notification that said 
      "Title: ${notifData.title}, 
      Body: ${notifData.body}" 
      Based on that, the user now asks: 
      ${messageToSend}`;
    }

    const typingTimer = setTimeout(() => setIsTyping(true), 300);

    try {
      const routeContext = await buildResolvedRouteContext(messageToSend, conversationBeforeReply);
      const shouldUseRegionalTransport =
        Boolean(
          routeContext?.origin &&
          routeContext?.destination &&
          !routeContext.withinBacolodCoverage
        );

      const regionalOrigin =
        routeContext?.origin?.resolvedLabel || routeContext?.origin?.normalized || "";
      const regionalDestination =
        routeContext?.destination?.resolvedLabel || routeContext?.destination?.normalized || "";

      const data = shouldUseRegionalTransport
        ? await askRegionalTransport(regionalOrigin, regionalDestination)
        : await askBacolodAssistant(messageWithContext, routeContext);

      localStorage.removeItem('lastPushNotification');

      let finalAnswer = data.answer || data.error || "Sorry, I didn't get that.";
      let routePreview: ChatJeepneyRoutePreview | undefined;
      let transportPreview: ChatTransportRoutePreview | undefined;
      const aiNeedsJeepneyRoute =
        !shouldUseRegionalTransport && "needsJeepneyRoute" in data
          ? data.needsJeepneyRoute
          : undefined;

      if (shouldUseRegionalTransport) {
        finalAnswer = normalizeOutsideCoverageTransportAnswer(finalAnswer, routeContext);
        // Show a route map for this regional/intercity trip.
        if (routeContext?.origin && routeContext?.destination) {
          transportPreview = {
            originLabel: cleanTransportLabel(
              regionalOrigin || routeContext.origin.normalized || "Origin"
            ),
            destinationLabel: cleanTransportLabel(
              regionalDestination || routeContext.destination.normalized || "Destination"
            ),
            origin: { lat: routeContext.origin.lat, lng: routeContext.origin.lng },
            destination: { lat: routeContext.destination.lat, lng: routeContext.destination.lng },
          };
        }
      }
      const shouldAutoAppendLocalJeepneyRoute = Boolean(
        aiNeedsJeepneyRoute === undefined &&
        !shouldUseRegionalTransport &&
        routeContext?.origin &&
        routeContext?.destination &&
        routeContext.withinBacolodCoverage &&
        isDirectionIntentLike(messageToSend)
      );

      if (
        aiNeedsJeepneyRoute === true ||
        shouldAutoAppendLocalJeepneyRoute
      ) {
        let routeAppended = false;
        try {
          const routeBundle = await buildChatJeepneyRouteBundle(
            messageToSend,
            conversationBeforeReply,
            routeContext
          );
          if (routeBundle) {
            finalAnswer = appendJeepneyRouteToAnswer(finalAnswer, routeBundle.text);
            routePreview = routeBundle.preview;
            routeAppended = true;
          }
        } catch (routeError) {
          console.error("Failed to attach jeepney route:", routeError);
        }

        // The AI shaped its reply as a short lead-in expecting an appended
        // Bacolod jeepney route. If we couldn't build one (trip outside jeepney
        // coverage, e.g. the airport, or an endpoint that didn't resolve), don't
        // leave that dangling promise — call askTransport and show its answer.
        if (!routeAppended) {
          const fallback = await buildNoJeepneyFallbackAnswer(
            routeContext,
            messageToSend,
            conversationBeforeReply,
            finalAnswer,
            (data as AskBacolodResponse).origin,
            (data as AskBacolodResponse).destination
          );
          finalAnswer = fallback.answer;
          if (fallback.transportPreview) {
            transportPreview = fallback.transportPreview;
          }
        }
      }

      // Keep the typing indicator up until the FULL answer is ready — including
      // any async jeepney-route lookup / askTransport fallback above — so it
      // doesn't vanish while the AI is still "thinking".
      clearTimeout(typingTimer);
      setIsTyping(false);

      const aiMessage: Message = {
        from: "ai",
        text: finalAnswer,
        routePreview,
        transportPreview,
      };
      setMessages((prev) => [...prev, aiMessage]);

      if (routePreview) track("jeepney_route_shown");
      else if (transportPreview) track("transport_answer_shown");

    } catch (error) {
      track("chat_request_failed");
      console.error("Request failed after retries", error);
      clearTimeout(typingTimer);
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        { from: "ai", text: "Sorry, I'm having trouble connecting. Please try again." },
      ]);
    }

    setIsSending(false);
  };

  const handlePlaceClick = async (place: { name: string; vicinity: string; place_id?: string }) => {
    if (isKeyboardOpen.current) {
      Keyboard.hide();
      return;
    }

    track("place_tapped", { name: place.name });

    // Check if vicinity has multiple locations (separated by ;)
    const vicinities = place.vicinity.split(';').map((v: string) => v.trim()).filter((v: string) => v);

    if (vicinities.length > 1) {
      // Show selector for multiple vicinities
      setPendingPlace(place);
      setVicinityOptions(vicinities);
      setIsVicinitySelectorOpen(true);
      return;
    }

    // Single vicinity - proceed as before
    setShowLoading(true);
    try {
      const res = await fetch("https://placedetails-lrenkrr2bq-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          place.place_id
            ? { place_id: place.place_id }
            : { name: place.name, vicinity: place.vicinity }
        ),
      });

      const data = await res.json();
      console.log("Place details:", data);
      setSelectedPlace(data);
      setIsPlaceModalOpen(true);
    } catch (err) {
      console.error("Place details error:", err);
    }
    setShowLoading(false);
  };

  const handleVicinitySelect = async (vicinity: string) => {
    if (!pendingPlace) return;

    setIsVicinitySelectorOpen(false);
    setShowLoading(true);
    try {
      const res = await fetch("https://placedetails-lrenkrr2bq-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          pendingPlace.place_id
            ? { place_id: pendingPlace.place_id }
            : { name: pendingPlace.name, vicinity }
        ),
      });

      const data = await res.json();
      console.log("Place details:", data);
      setSelectedPlace(data);
      setIsPlaceModalOpen(true);
    } catch (err) {
      console.error("Place details error:", err);
    }
    setShowLoading(false);
    setPendingPlace(null);
  };

  const getPlaceWithCoords = async (place: any) => {
    const key = getPlaceCacheKey(place);
    const coords = getPlaceCoordinates(place);

    if (coords) {
      const normalizedPlace = {
        ...place,
        lat: coords.lat,
        lng: coords.lng,
      };
      placeCoordsCacheRef.current.set(key, normalizedPlace);
      return normalizedPlace;
    }

    const cachedPlace = placeCoordsCacheRef.current.get(key);
    if (cachedPlace) return { ...place, ...cachedPlace };

    if (place.place_id) {
      console.warn("Place is missing coordinates despite having a place_id:", place.name);
      return place;
    }

    const existingRequest = placeGeometryRequestRef.current.get(key);
    if (existingRequest) return existingRequest;

    const request = fetch("https://placegeometry-lrenkrr2bq-uc.a.run.app", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: place.name, vicinity: place.vicinity }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || `HTTP ${res.status}`);
        }

        const geometryCoords = getPlaceCoordinates(data);
        const placeWithCoords = {
          ...place,
          place_id: place.place_id || data.place_id,
          lat: geometryCoords?.lat,
          lng: geometryCoords?.lng,
        };

        if (hasCoordinates(placeWithCoords)) {
          placeCoordsCacheRef.current.set(key, {
            ...placeWithCoords,
            lat: Number(placeWithCoords.lat),
            lng: Number(placeWithCoords.lng),
          });
        }

        return placeWithCoords;
      })
      .catch((err) => {
        console.error("Failed to get place geometry:", place.name, err);
        return place;
      })
      .finally(() => {
        placeGeometryRequestRef.current.delete(key);
      });

    placeGeometryRequestRef.current.set(key, request);
    return request;
  };

  const handleMapClick = async (places: any[]) => {
    const uniquePlaces = Array.from(
      new Map(places.map((place) => [getPlaceCacheKey(place), place])).values()
    );

    setShowLoading(true);
    try {
      const placesWithCoords = await Promise.all(uniquePlaces.map(getPlaceWithCoords));

      setMapPlaces(placesWithCoords.filter(hasCoordinates));
      setIsMapModalOpen(true);
    } finally {
      setShowLoading(false);
    }
  };

  function tryParseAIResponse(text: any) {
    if (text && typeof text === "object" && Array.isArray(text.recommendations)) return text;

    if (typeof text === "string") {
      // Try direct parse first
      try {
        let parsed = JSON.parse(text);
        if (Array.isArray(parsed)) parsed = parsed[0];
        if (parsed?.recommendations && Array.isArray(parsed.recommendations)) return parsed;
      } catch {
        // Do Nothing
      }

      // Extract JSON from inside ```json ... ``` code block
      const codeBlockMatch = text.match(/```json\s*([\s\S]*?)```/);
      if (codeBlockMatch) {
        try {
          let parsed = JSON.parse(codeBlockMatch[1].trim());
          if (Array.isArray(parsed)) parsed = parsed[0];
          if (parsed?.recommendations && Array.isArray(parsed.recommendations)) return parsed;
        } catch {
          // Do Nothing
        }
      }

      // Extract JSON object anywhere in the string (handles text before/after JSON)
      const jsonMatch = text.match(/\{[\s\S]*"recommendations"[\s\S]*\}/);
      if (jsonMatch) {
        try {
          let parsed = JSON.parse(jsonMatch[0]);
          if (Array.isArray(parsed)) parsed = parsed[0];
          if (parsed?.recommendations && Array.isArray(parsed.recommendations)) return parsed;
        } catch {
          // Do Nothing
        }
      }
    }

    return null;
  }

  useEffect(() => {
    // Update getLocation to not set fallback
    const getLocation = async () => {
      try {
        if (Capacitor.getPlatform() === 'web') {
          navigator.geolocation.getCurrentPosition(
            (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => setLocation(null) // denied or unavailable
          );
        } else {
          const { Geolocation } = await import('@capacitor/geolocation');
          const pos = await Geolocation.getCurrentPosition();
          setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        }
      } catch {
        setLocation(null);
      }
    };

    getLocation();
  }, []);

  useEffect(() => {
    contentRef.current?.scrollToBottom(300);
  }, [messages, isTyping]);

  useEffect(() => {
    if (Capacitor.getPlatform() === "web") return;

    let showSub: any;
    let hideSub: any;

    const setupListeners = async () => {
      showSub = await Keyboard.addListener('keyboardWillShow', (info) => {
        isKeyboardOpen.current = true;
        // Only iOS needs this: on Android resizeOnFullScreen already lifts the
        // webview above the keyboard (see the footer marginBottom below).
        setKeyboardHeight(info.keyboardHeight);
        setTimeout(() => contentRef.current?.scrollToBottom(300), 150);
      });

      hideSub = await Keyboard.addListener('keyboardWillHide', () => {
        isKeyboardOpen.current = false;
        setKeyboardHeight(0);
        setTimeout(() => contentRef.current?.scrollToBottom(300), 150);
      });
    };

    setupListeners();

    return () => {
      showSub?.remove();
      hideSub?.remove();
    };
  }, []);

  // ✅ Auto-scroll to bottom on new message
  useEffect(() => {
    contentRef.current?.scrollToBottom(300);
  }, [messages]);

  // 🔔 Push notification handling starts here
  // 🧩 Listen for push notifications when Home screen is active
  useEffect(() => {
    // Check if app was opened via a notification tap
    const stored = localStorage.getItem("latestNotification");
    if (stored) {
      try {
        const notif = JSON.parse(stored);
        let title = "";
        let body = "";
        if (Capacitor.getPlatform() === "android") {
          title = notif.title || "Notification";
          body = notif.body || "You have a new message.";
          // Store notification content in localStorage
          localStorage.setItem('lastPushNotification', JSON.stringify({ title, body }));
        } else {
          title = notif.aps?.alert?.title || "Notification";
          body = notif.aps?.alert?.body || "You have a new message.";
        }

        // Add message from notification
        setMessages((prev) => [
          ...prev,
          { from: "ai", text: `**${title}**\n\n${body}` },
        ]);

        // Clear it so it doesn’t repeat next time
        localStorage.removeItem("latestNotification");
      } catch (e) {
        console.error("Failed to parse notification:", e);
      }
    }

    // Listen for notifications tapped while Home is already open
    const handlePushTap = (event: any) => {
      const notif = event.detail?.payload;
      const title = notif?.title || "Notification";
      const body = notif?.body || "You have a new message.";

      setMessages((prev) => [
        ...prev,
        { from: "ai", text: `**${title}**\n\n${body}` },
      ]);
    };

    window.addEventListener("pushNotificationAction", handlePushTap);

    return () => {
      window.removeEventListener("pushNotificationAction", handlePushTap);
    };
  }, []);
  // 🔔 Push notification handling ends here

  useEffect(() => {
    if (hasShownClickHint) return;

    const lastIndex = messages.length - 1;
    const lastMsg = messages[lastIndex];
    if (!lastMsg || lastMsg.from !== 'ai') return;

    const parsed = tryParseAIResponse(lastMsg.text);
    const hasPlaces = parsed?.recommendations?.some((p: any) => p.isPlace);
    if (!hasPlaces) return;

    setGlowMessageIndex(lastIndex);
    const timer = setTimeout(() => {
      setGlowMessageIndex(null);
      setHasShownClickHint(true);
      localStorage.setItem('hasShownClickHint', 'true');
    }, 10000);

    return () => clearTimeout(timer);
  }, [messages]);

  useEffect(() => {
    const greeted = localStorage.getItem("bacolod_ai_greeted");
    const hasSeenTutorial = localStorage.getItem("hasSeenTutorial");

    if (!greeted && hasSeenTutorial) {
      const welcomeMessage: Message = {
        from: "ai",
        text: `👋 Hi there! I'm your **Bacolod AI Guide**.

I can help you discover things around **Bacolod City**, such as:
        
    • 🍽 Restaurants
    • ☕ Cafés
    • 🏨 Hotels
    • 🍻 Bars
    • 📍 Tourist spots
    • 🚗 Tours and services
    • 🚇 Commute options

But feel free to ask **anything about Bacolod** — recommendations, directions, hidden gems, local tips, and more.

    How can I help you today? 😊`
      };

      setMessages([welcomeMessage]);
      localStorage.setItem("bacolod_ai_greeted", "true");
    }
  }, []);

  // Replace your existing background useEffect with this one
  useEffect(() => {
    const container = bgRef.current;
    if (!container) return;

    const slides = Array.from(container.children) as HTMLElement[];
    if (!slides.length) return;

    let current = 0;
    // Show first slide immediately
    slides[current].style.opacity = '1';

    const advance = () => {
      slides[current].style.opacity = '0';
      current = (current + 1) % slides.length;
      slides[current].style.opacity = '1';
    };

    const interval = setInterval(advance, 5000);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        slides[current].style.opacity = '1';
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const hasChat = messages.length > 0;

  return (
    <IonPage className={`ai-chat-page ${hasChat ? "with-overlay" : ""}`}>
      <div ref={bgRef} className="bg-wrapper">
        <div className="bg-slide bg1"></div>
        <div className="bg-slide bg2"></div>
        <div className="bg-slide bg3"></div>
        <div className="bg-slide bg4"></div>
        <div className="bg-slide bg5"></div>
        <div className="bg-slide bg6"></div>
        <div className="bg-slide bg7"></div>
      </div>
      <IonHeader className="class-header">
        <IonToolbar className="chat-header"></IonToolbar>
      </IonHeader>

      <IonContent
        className="chat-content"
        fullscreen ref={contentRef}
        scrollEvents={true}
      >
        <div className="chat-layout">
        <div className="chat-column">
        {messages.map((msg, index) => {
          const parsed = msg.from === "ai" ? tryParseAIResponse(msg.text) : null;
          const hasRoutePreview = Boolean(msg.routePreview);
          const hasTransportPreview = Boolean(msg.transportPreview);
          const hasMapPlaces = Boolean(
            parsed?.recommendations?.some((p: any) => p.isPlace)
          );
          const showMapButton = hasRoutePreview || hasTransportPreview || hasMapPlaces;

          return (
            <div
              key={index}
              className={`chat-row ${msg.from === "user" ? "user-row" : "ai-row"}`}
            >

              {/* ✅ AI MESSAGE */}
              {msg.from === "ai" ? (
                <div className={`ai-bubble-wrapper ${showMapButton ? "has-map-action" : ""}`}>
                  <div className="chat-bubble chat-bubble-anim ai-bubble">
                    {parsed ? (
                      <div>
                        {parsed.intro && (
                          <div className="place-markdown" style={{ fontSize: "1em", color: "white", padding: "0 0 0.5em 0" }}>
                            <ReactMarkdown remarkPlugins={[remarkBreaks]}>{parsed.intro}</ReactMarkdown>
                          </div>
                        )}

                        {parsed.recommendations.length > 0 && (() => {
                          // find index of last isPlace item
                          const lastPlaceIndex = parsed.recommendations
                            .map((p: any, i: number) => p.isPlace ? i : -1)
                            .filter((i: number) => i !== -1)
                            .pop() ?? -1;

                          return (
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              {parsed.recommendations.map((place: any, i: number) => {
                                const isLastPlace = i === lastPlaceIndex;
                                const isGlowing = glowMessageIndex === index && isLastPlace;
                                const recommendationKey =
                                  place.place_id ||
                                  `${place.name || "recommendation"}-${place.vicinity || "no-vicinity"}-${i}`;

                                return (
                                  <div
                                    key={recommendationKey}
                                    className={isGlowing ? "place-item-glow" : ""}
                                    onClick={() => place.isPlace && handlePlaceClick({
                                      name: place.name,
                                      vicinity: place.vicinity,
                                      place_id: place.place_id
                                    })}
                                    style={{
                                      padding: "0.5em 0.1em",
                                      cursor: place.isPlace ? "pointer" : "default",
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 4,
                                      borderTop: i === 0 ? "2px dashed rgba(255,255,255,0.3)" : "none",
                                      borderBottom:
                                        i !== parsed.recommendations.length - 1
                                          ? "1px solid rgba(255,255,255,0.5)"
                                          : "2px dashed rgba(255,255,255,0.3)",
                                    }}
                                  >
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                      <span
                                        style={{
                                          fontSize: place.isPlace ? "1em" : "1.1em",
                                          fontWeight: 700,
                                          color: "white",
                                          lineHeight: 1.3,
                                          fontStyle: place.isPlace ? "normal" : "italic",
                                          padding: place.isPlace ? "0.1em 0.5em" : "0",
                                          marginRight: place.isPlace ? "0.5em" : "0",
                                          borderRadius: "0.8em",
                                          background: place.isPlace ? "rgba(237, 75, 130, 0.4)" : "transparent",
                                          width: "fit-content",
                                        }}
                                      >
                                        {place.name}
                                      </span>
                                      {place.isPlace && (
                                        <span style={{ color: "pink", opacity: 0.7, fontSize: "1.5em" }}>›</span>
                                      )}
                                    </div>
                                    {place.vicinity && (
                                      <span style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", display: "flex", alignItems: "center", gap: 4, overflow: "hidden" }}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5DCAA5" strokeWidth="2" style={{ flexShrink: 0 }}>
                                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                                          <circle cx="12" cy="9" r="2.5" />
                                        </svg>
                                        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                                          {place.vicinity}
                                        </span>
                                      </span>
                                    )}

                                    <div className="place-markdown" style={{ margin: 0, fontSize: "1em", color: place.isPlace ? "white" : "lightgray" }}>
                                      <ReactMarkdown remarkPlugins={[remarkBreaks]}>{place.description}</ReactMarkdown>
                                    </div>
                                  </div>
                                );
                              })}

                              {/* Tap hint label — shown once below the whole list */}
                              {glowMessageIndex === index && (
                                <div className="tap-hint-label">
                                  <svg className="tap-hint-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5DCAA5" strokeWidth="2">
                                    <path d="M9 18l6-6-6-6" />
                                  </svg>
                                  Tap a place to view more details
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {parsed.footer && (
                          <div className="place-markdown" style={{ fontSize: "1em", color: "white", padding: "0.5em 0 0 0" }}>
                            <ReactMarkdown remarkPlugins={[remarkBreaks]} rehypePlugins={[rehypeRaw]}>{parsed.footer}</ReactMarkdown>
                          </div>
                        )}
                      </div>
                    ) : (
                      <ReactMarkdown remarkPlugins={[remarkBreaks]} rehypePlugins={[rehypeRaw]}>
                        {typeof msg.text === "string" ? msg.text : ""}
                      </ReactMarkdown>
                    )}
                  </div>

                  {/* ✅ FLOATING AVATAR */}
                  <img
                    src="/assets/ai-avatar.png"
                    alt="AI"
                    className="ai-avatar-floating"
                  />

                  {/* Map Icon - show for route previews or place recommendations */}
                  {showMapButton && (
                    <IonButton
                      className="message-map-button"
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (msg.routePreview) {
                          track("route_map_opened", { type: "jeepney" });
                          setSelectedRoutePreview(msg.routePreview);
                          return;
                        }
                        if (msg.transportPreview) {
                          track("route_map_opened", { type: "transport" });
                          setSelectedTransportPreview(msg.transportPreview);
                          return;
                        }
                        if (!parsed) return;
                        track("places_map_opened");
                        handleMapClick(parsed.recommendations.filter((p: any) => p.isPlace));
                      }}
                    >
                      <IonIcon icon={mapOutline} />
                    </IonButton>
                  )}
                </div>
              ) : (
                /* ✅ USER MESSAGE */
                <>
                  <div className="chat-bubble chat-bubble-anim user-bubble">
                    <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                      {typeof msg.text === "string" ? msg.text : ""}
                    </ReactMarkdown>
                  </div>

                  <img
                    src="/assets/user-avatar.png"
                    alt="User"
                    className="chat-avatar"
                  />
                </>
              )}
            </div>
          );
        })}

        {isTyping && (
          <div className="chat-row ai-row typing">
            <img src="/assets/ai-avatar.png" alt="AI" className="chat-avatar" />
            <div className="chat-bubble ai-bubble typing-bubble">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
          </div>
        )}

        {/* Custom Loading */}
        <MasskaraLoading isOpen={showLoading} backdrop={true} />
        </div>

        <aside className="chat-updates-panel">
          <div className="chat-updates-card">
            <div className="chat-updates-scroll">
              <CityUpdatesFeed />
            </div>
          </div>
        </aside>
        </div>
      </IonContent>

      <IonFooter
        className="chat-footer"
        style={{
          transition: "margin-bottom 0.25s ease",
          // iOS: the webview does NOT auto-resize (resizeOnFullScreen is
          // Android-only), so lift the composer above the keyboard manually.
          // Android: resizeOnFullScreen already lifts the webview, so adding a
          // margin here would double/triple-compensate (the original bug).
          marginBottom:
            Capacitor.getPlatform() === "ios" && keyboardHeight
              ? `${keyboardHeight}px`
              : "0px",
        }}
      >
        <IonToolbar className="footer-toolbar">
          <div className="composer-row" style={{ display: "flex", alignItems: "center", paddingBottom: "8px" }}>
            <IonInput
              mode="ios"
              className="chat-input"
              placeholder="Ask me anything about Bacolod City!"
              value={input}
              onIonInput={(e: any) => setInput(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter") {
                  e.preventDefault(); // prevent new line or form submit
                  if (!isSending) sendMessage();
                }
              }}
              style={{
                flex: 1,
                borderRadius: "20px",
                padding: "8px 12px",
                marginRight: "0px",
                fontSize: "16px",
              }}
            />
            <IonButton
              onClick={sendMessage}
              disabled={isSending}
              fill="solid"
              color="secondary"
              className="btn-send"
              style={{
                borderRadius: "50%",
                width: "46px",
                height: "46px",
                minWidth: "46px",
                minHeight: "46px",
              }}
            >
              <IonIcon className="icon-send" icon={send} />
            </IonButton>
          </div>
        </IonToolbar>
      </IonFooter>
      <PlaceDetailModal
        isOpen={isPlaceModalOpen}
        place={selectedPlace}
        onClose={() => {
          setIsPlaceModalOpen(false);
          setSelectedPlace(null);
        }}
      />

      {/* Festive Vicinity Selector Modal - Bacolod Masskara Theme */}
      <IonModal
        isOpen={isVicinitySelectorOpen}
        onDidDismiss={() => setIsVicinitySelectorOpen(false)}
        initialBreakpoint={0.5}
        breakpoints={[0, 0.5, 1]}
        className="vicinity-selector-modal"
      >
        <IonContent className="vicinity-selector-content">
          <div className="vicinity-selector">

            {/* Festive Header */}
            <div style={{
              padding: "24px 20px 16px",
              textAlign: "center",
              position: "relative",
              overflow: "hidden"
            }}>
              {/* Decorative sparkles */}
              <div style={{ position: "absolute", top: 10, left: 20, color: "#f9a825", fontSize: "16px" }}>✦</div>
              <div style={{ position: "absolute", top: 15, right: 25, color: "#ed4b82", fontSize: "14px" }}>★</div>
              <div style={{ position: "absolute", bottom: 20, left: 30, color: "#43ae91", fontSize: "12px" }}>✦</div>
              <div style={{ position: "absolute", top: 25, left: 40, color: "#f9a825", fontSize: "10px" }}>★</div>

              <div style={{
                background: "linear-gradient(135deg, rgba(237, 75, 130, 0.2), rgba(249, 168, 37, 0.2))",
                padding: "12px 20px",
                borderRadius: "20px",
                display: "inline-block",
                border: "1px solid rgba(237, 75, 130, 0.7)"
              }}>
                <span style={{ fontSize: "24px", marginRight: "8px" }}>🎭</span>
                <span style={{
                  color: "#fff",
                  fontSize: "18px",
                  fontWeight: 600,
                  background: "linear-gradient(90deg, #ed4b82, #f9a825)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent"
                }}>
                  {pendingPlace?.name}
                </span>
                <span style={{ fontSize: "24px", marginLeft: "8px" }}>🎭</span>
              </div>

              <p style={{
                color: "rgba(255,255,255,0.9)",
                marginTop: "16px",
                fontSize: "14px"
              }}>
                This place has multiple locations — choose one to explore
              </p>
            </div>

            {/* Location Options */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "1em" }}>
              {vicinityOptions.map((vicinity, index) => (
                <div
                  key={index}
                  onClick={() => handleVicinitySelect(vicinity)}
                  style={{
                    padding: "16px 20px",
                    background: "linear-gradient(135deg, rgba(67, 174, 145, 0.15), rgba(67, 174, 145, 0.05))",
                    borderRadius: "16px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    border: "1px solid rgba(67, 174, 145, 0.3)",
                    transition: "all 0.2s ease",
                    position: "relative",
                    overflow: "hidden"
                  }}
                >
                  {/* Decorative dot */}
                  <div style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: index === 0 ? "#ed4b82" : index === 1 ? "#f9a825" : "#43ae91",
                    boxShadow: `0 0 10px ${index === 0 ? "#ed4b82" : index === 1 ? "#f9a825" : "#43ae91"}`
                  }} />

                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#43ae91" strokeWidth="2">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                    <circle cx="12" cy="9" r="2.5" />
                  </svg>

                  <span style={{
                    color: "#fff",
                    fontSize: "14px",
                    fontWeight: 500,
                    flex: 1
                  }}>{vicinity}</span>

                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </div>
              ))}
            </div>

            {/* Cancel Button */}
            <IonButton
              expand="block"
              fill="clear"
              onClick={() => setIsVicinitySelectorOpen(false)}
              style={{
                marginTop: "1.1em",
                color: "white",
                fontSize: "0.9em",
                fontWeight: 700
              }}
            >
              BACK
            </IonButton>

          </div>
        </IonContent>
      </IonModal>

      <MapModal
        isOpen={isMapModalOpen}
        places={mapPlaces}
        userLocation={location}
        onClose={() => setIsMapModalOpen(false)}
      />
      <ChatRouteModal
        isOpen={Boolean(selectedRoutePreview)}
        route={selectedRoutePreview}
        onClose={() => setSelectedRoutePreview(null)}
      />

      <TransportRouteModal
        isOpen={Boolean(selectedTransportPreview)}
        route={selectedTransportPreview}
        onClose={() => setSelectedTransportPreview(null)}
      />
    </IonPage>
  );
};

export default Chat;
